import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { cn } from "@/common/utils/cn";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useCreateDashboard } from "../../hooks/dashboards/useCreateDashboard";
import { createDashboardSchema, CreateDashboardValidationSchema } from "../../schemas/dashboards/dashboard.schema";

interface CreateDashboardProps {
  outlineButton?: boolean;
  onCreateDashboard: (dashboardId: string) => void;
}

const CreateDashboard = ({
  outlineButton,
  onCreateDashboard,
}: CreateDashboardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: {
      errors,
    },
  } = useForm({
    resolver: zodResolver(createDashboardSchema),
    defaultValues: {
      display_name: '',
    },
  });

  const {
    mutate: createDashboard,
    isPending: createDashboardIsPending,
  } = useCreateDashboard({
    options: {
      onSuccess: (response) => {
        const dashboardId = response?.data?.data?.id;

        queryClient.invalidateQueries({
          queryKey: ['dashboards'],
        });

        setIsOpen(false);

        onCreateDashboard(dashboardId);
      },
      onError: FormTransformers.errorsTransformer({
        setError,
        setMessages,
      }),
    },
  });

  const onSubmit: SubmitHandler<CreateDashboardValidationSchema> = (data) => createDashboard(data);

  useEffect(() => {
    reset();
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={(
        <Button
          className={cn(
            'transition-colors',
            outlineButton && 'border border-blue-800 text-blue-800 bg-transparent hover:bg-blue-800/10'
          )}
          operationKlassToCheckPermission="Client::Dashboards::Create"
        >
          Novo dashboard
        </Button>
      )}
      title="Criar dashboard"
      className="rounded-lg overflow-hidden"
      bodyClassName="text-white-800"
      showClose={false}
      useCancellableFooter
      cancelButtonClassName="bg-white-200"
      confirmButtonClassName="bg-primary-blue"
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={createDashboardIsPending}
      body={(
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            label="Nome do dashboard"
            markAsRequired
            autoFocus
            {...register('display_name')}
            errorMessage={errors?.display_name?.message}
          />
        </form>
      )}
    />
  );
}

export default CreateDashboard;
